import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { IFeedbackStudent, IFeedbackTutor } from '../helper';
import { ILessonStudentFeedback } from '../../../../store/V2/LessonStudentFeedbacksSlice';
import AddStudentFeedbackForm from './AddStudentFeedbackForm';
import { TPAutoCompleteArrowIconComponent, TPTextField } from '../../../Inputs';
import { getShortName } from '../../../../helper';
import { IUser } from '../../../../store/UserSlice';

interface AddStudentFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  students: IFeedbackStudent[];
  tutors?: IFeedbackTutor[];
  feedback?: ILessonStudentFeedback;
  onFeedbackAdded?: (feedbackData: ILessonStudentFeedback[]) => void;
  onFeedbackUpdated?: (
    id: string,
    feedbackData: ILessonStudentFeedback
  ) => void;
}

const AddStudentFeedbackModal: FunctionComponent<AddStudentFeedbackModalProps> = ({
  open,
  onClose,
  sectionId,
  students,
  tutors = [],
  feedback,
  onFeedbackAdded,
  onFeedbackUpdated,
}) => {
  // State
  const [selectedTutor, setSelectedTutor] = useState<IFeedbackTutor | null>(
    null
  );

  const mode = feedback ? 'edit' : 'new';

  useEffect(() => {
    if (mode === 'edit' && feedback) {
      const _tutor = feedback.tutor as IUser;

      setSelectedTutor({
        _id: _tutor._id,
        firstname: _tutor.firstname,
        lastname: _tutor.lastname,
      });
    }
  }, []);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '520px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {mode === 'edit' ? 'Edit feedback' : 'New feedback'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '0 15px', sm: '0 30px' } }}>
        <Box sx={{ pb: { xs: '15px', sm: '30px' } }}>
          {mode !== 'edit' ? (
            <Stack pt="6px" pb="10px">
              <Autocomplete
                fullWidth
                options={tutors}
                filterSelectedOptions
                value={selectedTutor}
                popupIcon={TPAutoCompleteArrowIconComponent}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                onChange={(e, value) => setSelectedTutor(value)}
                getOptionLabel={(option) =>
                  getShortName(option.firstname, option.lastname, true)
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    InputLabelProps={{ shrink: true }}
                    label="Tutor *"
                    placeholder="Select"
                  />
                )}
              />
            </Stack>
          ) : null}
          {selectedTutor ? (
            <AddStudentFeedbackForm
              sectionId={sectionId}
              students={students}
              tutor={selectedTutor}
              feedback={feedback}
              onFeedbackAdded={
                onFeedbackAdded
                  ? (feedbackData) => {
                      onFeedbackAdded(feedbackData);
                      onClose();
                    }
                  : undefined
              }
              onFeedbackUpdated={
                onFeedbackUpdated
                  ? (id, feedbackData) => {
                      onFeedbackUpdated(id, feedbackData);
                    }
                  : undefined
              }
            />
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentFeedbackModal;
